import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { getContainer } from '@framework/di/getContainer';
import ScorecardRepository from '@/creditflowresearch/infrastructure/repository/ScorecardRepository';
import CompanyRepository from '@/creditflowresearch/infrastructure/repository/CompanyRepository';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import { getErrorMessage } from '@framework/message/Mapper/getMessage';
import { REDIRECT_ERROR_URL_WRONG_ID } from '@/creditflowresearch/application/League/constants';
import { IScorecardSetters } from '@/creditflowresearch/application/League/Scorecard/types';
import { IStandingsPayload } from '@/creditflowresearch/domain/vo/League/IStandings';

export const useScorecardApi = (setters: IScorecardSetters) => {
  const container = getContainer();
  const router = useRouter();
  const scorecardRepository: ScorecardRepository = container.get(ScorecardRepository);
  const companyRepository: CompanyRepository = container.get(CompanyRepository);

  const getCompany = useCallback(async (payload: { viewId: number | string }) => {
    setters.setIsCompanyFetching(true);

    try {
      const response = await companyRepository.getCompanyByViewId(payload);

      if (!response) {
        throw new Error(REDIRECT_ERROR_URL_WRONG_ID);
      }

      setters.setCompanyData(response);
      setters.setCompanyError(response.id ? null : REDIRECT_ERROR_URL_WRONG_ID);
      setters.setIsCompanyFetching(false);

      return response;
    } catch (error) {
      setters.setIsCompanyFetching(false);

      if (error.message === REDIRECT_ERROR_URL_WRONG_ID) {
        await router.push('/404');
      }

      AlertManager.error(getErrorMessage(error));
      throw error;
    }
  }, [companyRepository, router, setters]);

  const getStandings = useCallback(async (payload: IStandingsPayload) => {
    setters.setInitialized(false);
    setters.setIsFetching(true);

    try {
      const response = await scorecardRepository.getStandings(payload);

      setters.setData(response);
      setters.setIsFetching(false);
      setters.setInitialized(true);

      return response;
    } catch (error) {
      setters.setIsFetching(false);
      setters.setInitialized(false);
      AlertManager.error(getErrorMessage(error));
      throw error;
    }
  }, [scorecardRepository, setters]);

  return {
    getCompany,
    getStandings,
  };
};
